import { Box, NavLink, Paper, Select, Stack, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconApi, IconDatabaseCog, IconDrone, IconFileCode, IconFiles, IconHistory, IconLayoutDashboard, IconLeaf, IconList, IconLock,
  IconMapPins, IconSettings, IconShieldCheck, IconShieldLock, IconStack2, IconTableImport, IconUsers, IconBriefcase,
  IconExternalLink, IconLanguage,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";

import { useCan } from "../../auth/user";
import { useT } from "../../i18n/script";

export interface AdminNavItem { to: string; label: string; icon: ReactNode; perm?: string; external?: boolean; desc?: string }
export interface AdminNavSection { title: string; items: AdminNavItem[] }

const I = 17;
/** Sitni stilovi samo za administraciju (hover redova u listama). */
const ADMIN_CSS = `.audit-row { transition: background .12s ease; }
.audit-row:hover { background: var(--mantine-color-default-hover) !important; }
.audit-row:focus-visible { outline: 2px solid var(--agri-apple, #c8453a); outline-offset: -2px; }`;
/** Struktura administracije — koristi je i pregled (AdminHub) za kartice brzog pristupa. */
export const ADMIN_SECTIONS: AdminNavSection[] = [
  { title: "", items: [{ to: "/admin", label: "Pregled", icon: <IconLayoutDashboard size={I} /> }] },
  { title: "Korisnici i pristup", items: [
    { to: "/admin/korisnici", label: "Korisnici", icon: <IconUsers size={I} />, perm: "admin:users", desc: "Nalozi, role, MFA i opseg po opštinama" },
    { to: "/admin/role", label: "Role i ovlašćenja", icon: <IconShieldCheck size={I} />, perm: "admin:roles", desc: "Poslovne role i funkcije koje smiju koristiti" },
    { to: "/admin/pravila", label: "Pravila pristupa podacima", icon: <IconLock size={I} />, perm: "admin:data-rules", desc: "GeoFence pravila za WMS/WFS slojeve" },
    { to: "/predmeti", label: "Predmeti", icon: <IconBriefcase size={I} />, perm: "cases:read", desc: "Zahtjevi za verifikaciju i rokovi obrade" },
  ] },
  { title: "Prostorni podaci", items: [
    { to: "/admin/slojevi", label: "Geoservisi i slojevi", icon: <IconStack2 size={I} />, perm: "layers:propose", desc: "Konekcije, uvoz fajlova i odobravanje slojeva" },
    { to: "/uav", label: "UAV snimanja", icon: <IconDrone size={I} />, perm: "uav:upload", desc: "Ortofoto, nDSM i multispektral po lokalitetima" },
    { to: "/uvoz", label: "Uvoz podataka", icon: <IconTableImport size={I} />, perm: "data:import", desc: "Excel/CSV obrasci, GIS granice, fotografije" },
  ] },
  { title: "Šifarnici i šabloni", items: [
    { to: "/admin/sifarnici", label: "Šifarnici", icon: <IconList size={I} />, perm: "registry:manage", desc: "Vrste, sorte, podloge, FFS, đubriva..." },
    { to: "/admin/opstine", label: "Opštine", icon: <IconMapPins size={I} />, perm: "registry:manage", desc: "Opštine i katastarske opštine" },
    { to: "/admin/fenologija", label: "Fenološki šabloni", icon: <IconLeaf size={I} />, perm: "phenology:read", desc: "BBCH faze po grupama voća" },
    { to: "/admin/dms", label: "Tipovi dokumenata", icon: <IconFiles size={I} />, perm: "dms:read", desc: "Rokovi čuvanja i arhivska knjiga" },
    { to: "/admin/pismo", label: "Izrazi bez transliteracije", icon: <IconLanguage size={I} />, perm: "registry:manage", desc: "Termini koji u ćirilici ostaju latinicom" },
    { to: "/admin/html-sabloni", label: "Šabloni dokumenata", icon: <IconFileCode size={I} />, perm: "reports:manage", desc: "HTML zaglavlja i stilovi izvještaja" },
  ] },
  { title: "Sistem", items: [
    { to: "/admin/podesavanja", label: "Podešavanja", icon: <IconSettings size={I} />, perm: "system:params", desc: "E-pošta, registracija, satelit, privatnost..." },
    { to: "/admin/audit", label: "Istorija aktivnosti", icon: <IconHistory size={I} />, perm: "audit:read", desc: "Ko je šta i kada uradio u sistemu" },
    { to: "/admin/licni-podaci", label: "Zaštita ličnih podataka", icon: <IconShieldLock size={I} />, desc: "Evidencija obrade i moji podaci" },
    { to: "/admin/backup", label: "Backup", icon: <IconDatabaseCog size={I} />, perm: "system:backup", desc: "Dnevni i ručni backup baze" },
    { to: "/api/docs", label: "API (Swagger)", icon: <IconApi size={I} />, external: true, desc: "REST API dokumentacija i testiranje" },
  ] },
];

export function useAdminSections(): AdminNavSection[] {
  const can = useCan();
  return ADMIN_SECTIONS.map((s) => ({ ...s, items: s.items.filter((i) => !i.perm || can(i.perm)) })).filter((s) => s.items.length);
}

function isActive(pathname: string, to: string) {
  if (to === "/admin") return pathname === "/admin" || pathname === "/admin/";
  return pathname === to || pathname.startsWith(to + "/");
}

/** Okvir administracije: lijevo podmeni grupisan po sekcijama (na telefonu padajuća lista), desno sadržaj. */
export default function AdminLayout({ children }: { children: ReactNode }) {
  const { t } = useT();
  const loc = useLocation();
  const nav = useNavigate();
  const sections = useAdminSections();
  const wide = useMediaQuery("(min-width: 62em)", true);
  const go = (it: AdminNavItem) => (it.external ? window.open(it.to, "_blank", "noopener") : nav(it.to));
  const all = sections.flatMap((s) => s.items);
  const current = all.find((i) => !i.external && isActive(loc.pathname, i.to));

  if (!wide) {
    return (
      <Stack gap="md">
        <style>{ADMIN_CSS}</style>
        <Select
          aria-label={t("Odjeljak administracije")}
          leftSection={current?.icon}
          value={current?.to ?? null}
          placeholder={t("Administracija")}
          allowDeselect={false}
          data={sections.map((s) => ({
            group: s.title ? t(s.title) : t("Administracija"),
            items: s.items.map((i) => ({ value: i.to, label: t(i.label) + (i.external ? " ↗" : "") })),
          }))}
          onChange={(v) => { const it = all.find((i) => i.to === v); if (it) go(it); }}
        />
        <Box miw={0}>{children}</Box>
      </Stack>
    );
  }

  return (
    <Box style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
      <style>{ADMIN_CSS}</style>
      <Paper component="nav" aria-label={t("Administracija")} withBorder radius="lg" p={8} w={236}
        style={{ position: "sticky", top: 72, flexShrink: 0, maxHeight: "calc(100vh - 88px)", overflowY: "auto",
          background: "var(--mantine-color-body)" }}>
        {sections.map((s, si) => (
          <Box key={s.title || si} mb={6}>
            {s.title && (
              <Text size="xs" fw={700} c="dimmed" tt="uppercase" px={10} pt={10} pb={4} style={{ letterSpacing: ".04em" }}>
                {t(s.title)}
              </Text>
            )}
            {s.items.map((it) => (
              <NavLink key={it.to} label={t(it.label)} leftSection={it.icon}
                rightSection={it.external ? <IconExternalLink size={13} opacity={0.6} /> : undefined}
                active={!it.external && isActive(loc.pathname, it.to)} variant="light" color="agri"
                href={it.to} onClick={(e) => { e.preventDefault(); go(it); }}
                styles={{ root: { borderRadius: 8, paddingTop: 6, paddingBottom: 6 }, label: { fontSize: 14 } }} />
            ))}
          </Box>
        ))}
      </Paper>
      <Box style={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}
